import { QueryClient } from 'react-query';
import { trackCacheMetric } from './track-cache-metric';

export class MonitoredQueryClient extends QueryClient {
  constructor(config?: ConstructorParameters<typeof QueryClient>[0]) {
    super(config);
    this.setupCacheSubscription();
  }

  private setupCacheSubscription() {
    this.getQueryCache().subscribe((event) => {
      if (!event) return;
      const { type, query } = event;

      // 1. Hook-based query mounts (useQuery, useInfiniteQuery)
      if (type === 'observerAdded') {
        const hasData = query.state.data !== undefined;
        const isStale = query.isStale();

        if (hasData && !isStale) {
          // Serves instantly from cache, no network fetch will trigger
          trackCacheMetric(query.queryKey, 'hit-fresh');
        }
      }

      // 2. Fetch action triggered (captures miss or hit-stale/revalidations)
      if (type === 'queryUpdated') {
        const action = (event as any).action;
        if (action && action.type === 'fetch') {
          const hasData = query.state.data !== undefined;
          trackCacheMetric(query.queryKey, hasData ? 'hit-stale' : 'miss');
        }
      }
    });
  }

  private getQueryKeyAndOptions(args: any[]): { queryKey: any; options?: any } {
    if (args.length === 0) {
      return { queryKey: undefined };
    }
    // Signature 1: fetchQuery(options)
    if (typeof args[0] === 'object' && args[0] !== null && 'queryKey' in args[0]) {
      return { queryKey: args[0].queryKey, options: args[0] };
    }
    // Signature 2 & 3: fetchQuery(queryKey, options?) / fetchQuery(queryKey, queryFn, options?)
    const queryKey = args[0];
    const options = args[2] || args[1];
    return { queryKey, options: typeof options === 'object' ? options : undefined };
  }

  private interceptImperativeQuery(args: any[]) {
    try {
      const { queryKey } = this.getQueryKeyAndOptions(args);
      if (!queryKey) return;

      const query = this.getQueryCache().find(queryKey);
      const hasData = query ? query.state.data !== undefined : false;

      if (hasData) {
        const isStale = query ? query.isStale() : true;
        if (!isStale) {
          // Direct hit-fresh that would normally not trigger any subscriber event or fetch action
          trackCacheMetric(queryKey, 'hit-fresh');
        }
      }
    } catch (err) {
      console.error('Error intercepting imperative query', err);
    }
  }

  override async fetchQuery(...args: any[]) {
    this.interceptImperativeQuery(args);
    return super.fetchQuery(...(args as [any, any?, any?]));
  }

  override async prefetchQuery(...args: any[]) {
    this.interceptImperativeQuery(args);
    return super.prefetchQuery(...(args as [any, any?, any?]));
  }

  override async fetchInfiniteQuery(...args: any[]) {
    this.interceptImperativeQuery(args);
    return super.fetchInfiniteQuery(...(args as [any, any?, any?]));
  }

  override async prefetchInfiniteQuery(...args: any[]) {
    this.interceptImperativeQuery(args);
    return super.prefetchInfiniteQuery(...(args as [any, any?, any?]));
  }
}
